import {
    App,
    Checkbox,
    DatePicker,
    Flex,
    Form,
    Input,
    Modal,
    Spin,
} from "antd";
import dayjs from "dayjs";
import React, {
    ComponentProps,
    FC,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    getPointInfoCollection,
    getSchedulerPointScheduleDetails,
    getTaskListsThunk,
    getWorkGetScheduleThunk,
    postFileUpload,
    postTaskCreateCustom,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import {
    AssignmentResponseType,
    AssignmentType,
    Option,
    Point,
    UserRole,
} from "tm-shared/types";

import { ROLE_CHILDREN_MAP } from "@/constants/domain";
import {
    ASSIGNMENT_RESPONSE_VOCABULARY,
    ASSIGNMENT_VOCABULARY,
} from "@/constants/ui/assignment";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { AppSelect } from "@/shared/forms/AppSelect";
import { BaseButton, PrimaryText } from "@/shared/ui";
import { UploadPhotoItem } from "@/types/common";
import { ModalPayloads, ModalType } from "@/types/modals";

import { PhotoUploadInput } from "./components/PhotoUploadField";
import { getCreateCustomTaskModalCapabilities } from "./CreateCustomTaskModal.policy";
import { useAvailableDates } from "./hooks/useAvailableDates";
import { StyledCustomTaskForm } from "./Styled";

// ─── Constants ────────────────────────────────────────────────────────────────

const ASSIGNMENT_TYPE_OPTIONS = Object.values(
    ASSIGNMENT_VOCABULARY,
) as Option<AssignmentType>[];

const ASSIGNMENT_RESPONSE_OPTIONS = Object.values(
    ASSIGNMENT_RESPONSE_VOCABULARY,
) as Option<AssignmentResponseType>[];

const ALL_ROLES: Option<string>[] = [
    { label: "Мерчандайзер", value: "md" },
    { label: "Торговий агент", value: "ta" },
];

const FIXED_RESPONSE_TYPES: Partial<
    Record<AssignmentType, AssignmentResponseType>
> = {
    [AssignmentType.InfoCommunication]: AssignmentResponseType.Text,
};

const MESSAGES = {
    required: "Обов'язкове поле",
    minLength: "Мінімум 3 символи",
    uploadError: "Помилка завантаження файлу",
    createError: "Помилка створення доручення",
    createSuccess: "Доручення успішно створено",
} as const;

const RichTextEditor = React.lazy(() =>
    import("@/shared/forms/RichTextEditor").then((module) => ({
        default: module.RichTextEditor,
    })),
);

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormValues {
    pointId: string;
    assignmentType: AssignmentType;
    assignmentResponse: AssignmentResponseType;
    roles: string[];
    title: string;
    visitDate: string;
    unplanned: boolean;
    description: string;
    photos: UploadPhotoItem[];
}

export interface CreateCustomTaskModalProps {
    payload: ModalPayloads[ModalType.CreateCustomTask];
    onCloseModal: () => void;
    onBack?: () => void;
}

const LazyRichTextEditorField = (
    props: ComponentProps<typeof RichTextEditor>,
) => (
    <Suspense
        fallback={
            <Flex
                align="center"
                justify="center"
                style={{
                    minHeight: 160,
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                }}
            >
                <Spin size="small" />
            </Flex>
        }
    >
        <RichTextEditor {...props} />
    </Suspense>
);

// ─── Component ────────────────────────────────────────────────────────────────

const CreateCustomTaskModal: FC<CreateCustomTaskModalProps> = ({
    payload,
    onCloseModal,
    onBack,
}) => {
    const { notification } = App.useApp();

    const { point, images, agentId, routeId } = payload;
    const role = useProtectedUserRoleSelector();
    const activeSchedule = useAppSelector((s) => s.manager.activeSchedule);
    const points = useAppSelector((s) => s.work.points);

    const dispatch = useAppDispatch();
    const capabilities = useMemo(
        () => getCreateCustomTaskModalCapabilities(role),
        [role],
    );

    // В agentMode point з payload може бути відсутній —
    // активний маркет визначається вибором у дропдауні
    const isAgentMode = Boolean(agentId);

    const isRolesInitializedRef = useRef(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [routePoints, setRoutePoints] = useState<Point[]>([]);
    const [isRoutePointsLoading, setIsRoutePointsLoading] = useState(false);

    // В звичайному режимі selectedPointId = point.id і не змінюється
    // В agentMode — визначається вибором у формі
    const [selectedPointId, setSelectedPointId] = useState<string | undefined>(
        isAgentMode ? undefined : point?.id,
    );

    const activePoint = useMemo<Point | undefined>(() => {
        if (!isAgentMode) return point;

        return routePoints.find((m) => m.id === selectedPointId);
    }, [isAgentMode, point, routePoints, selectedPointId]);

    const today = useMemo(() => dayjs().startOf("day"), []);

    const initialPhotos = useMemo<UploadPhotoItem[]>(
        () => images?.map((url: string) => ({ kind: "url", url })) ?? [],
        [images],
    );

    // Передаємо undefined поки маркет не вибраний —
    // useAvailableDates не буде робити запит
    const {
        dates: availableDatesByRole,
        roles: availableRoleSlugs,
        isLoading: isLoadingDates,
    } = useAvailableDates(activePoint?.id);

    const availableRoles = useMemo(
        () => ALL_ROLES.filter((r) => availableRoleSlugs.includes(r.value)),
        [availableRoleSlugs],
    );

    const [form] = Form.useForm<FormValues>();

    const [assignmentType, setAssignmentType] = useState<AssignmentType>(
        AssignmentType.Task,
    );
    const [roles, setRoles] = useState<string[]>([]);
    const [unplanned, setUnplanned] = useState(false);

    const isMultipleRoles = roles.length > 1;
    const mdWithoutDates = availableDatesByRole?.md?.length === 0;
    const isUnplannedForced = isMultipleRoles || mdWithoutDates;

    const availableDates = useMemo(() => {
        if (!roles.length) return [];
        return Array.from(
            new Set(roles.flatMap((r) => availableDatesByRole[r] ?? [])),
        );
    }, [roles, availableDatesByRole]);

    const availableDateSet = useMemo(
        () =>
            new Set(
                availableDates.map((d) =>
                    dayjs(d).format(backendDateFormat.date),
                ),
            ),
        [availableDates],
    );

    const firstValidDateIso = useMemo(() => {
        const sorted = availableDates
            .map((d) => dayjs(d).startOf("day"))
            .sort((a, b) => a.valueOf() - b.valueOf());
        const valid = sorted.find((d) => !d.isBefore(today));
        return (valid ?? today).toISOString();
    }, [availableDates, today]);

    // Завантаження маркетів агента
    useEffect(() => {
        if (!agentId || !routeId) return;

        const fetchRoutePoints = async () => {
            try {
                setIsRoutePointsLoading(true);

                const { result: schedules } =
                    await getSchedulerPointScheduleDetails({
                        routeId,
                    });

                const { result: pointsData } = await getPointInfoCollection({
                    ids: schedules?.map((item) => item?.pointId),
                });

                setRoutePoints(pointsData);
            } catch (error) {
                console.error(error);
            } finally {
                setIsRoutePointsLoading(false);
            }
        };

        fetchRoutePoints();
    }, [agentId, routeId]);

    // Скидаємо ролі і дату при зміні вибраного маркету
    useEffect(() => {
        if (!selectedPointId) return;

        isRolesInitializedRef.current = false;
        setRoles([]);
        form.setFieldsValue({ roles: [], visitDate: today.toISOString() });
    }, [selectedPointId, form, today]);

    // Ініціалізація ролей після завантаження дат
    useEffect(() => {
        const shouldSkip =
            isRolesInitializedRef.current ||
            isLoadingDates ||
            roles.length > 0 ||
            availableRoles.length === 0;

        if (shouldSkip) return;

        const firstRole = availableRoles[0];

        const preferChildrenRole = ROLE_CHILDREN_MAP[role];

        let initialRoles: string[] = [];

        if (availableRoles.length === 1 && firstRole) {
            initialRoles = [firstRole.value];
        } else if (preferChildrenRole) {
            initialRoles = [preferChildrenRole];
        }

        form.setFieldValue("roles", initialRoles);
        setRoles(initialRoles);
        isRolesInitializedRef.current = true;
    }, [roles.length, availableRoles, isLoadingDates, form, role]);

    useEffect(() => {
        if (isUnplannedForced) {
            form.setFieldValue("unplanned", true);
            setUnplanned(true);
        }
    }, [isUnplannedForced, form]);

    useEffect(() => {
        const fixed = FIXED_RESPONSE_TYPES[assignmentType];
        if (fixed) form.setFieldValue("assignmentResponse", fixed);
    }, [assignmentType, form]);

    useEffect(() => {
        if (isLoadingDates || !roles.length) return;

        const current = dayjs(form.getFieldValue("visitDate")).startOf("day");
        const isPast = current.isBefore(today);
        const isAllowed = availableDateSet.has(
            current.format(backendDateFormat.date),
        );

        if (!unplanned && !isMultipleRoles && !mdWithoutDates) {
            if (isPast || !isAllowed)
                form.setFieldValue("visitDate", firstValidDateIso);
        } else if (isPast) {
            form.setFieldValue("visitDate", today.toISOString());
        }
    }, [
        unplanned,
        isMultipleRoles,
        mdWithoutDates,
        isLoadingDates,
        roles.length,
        availableDateSet,
        firstValidDateIso,
        today,
        form,
    ]);

    const shouldDisableDate = useCallback(
        (date: dayjs.Dayjs) => {
            if (date.isBefore(today)) return true;
            if (unplanned || isMultipleRoles || mdWithoutDates) return false;
            return !availableDateSet.has(date.format(backendDateFormat.date));
        },
        [today, unplanned, isMultipleRoles, mdWithoutDates, availableDateSet],
    );

    const uploadPhoto = useCallback(
        async (photo: UploadPhotoItem): Promise<string> => {
            if (photo.kind === "url") return photo.url;
            try {
                const base64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const result =
                            typeof reader.result === "string"
                                ? reader.result
                                : "";
                        const base64 = result.split(",")[1] ?? "";

                        resolve(base64);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(photo.file);
                });
                const { result } = await postFileUpload({
                    space: activePoint!.id,
                    filename: photo.file.name,
                    base64Content: base64,
                });
                return result;
            } catch {
                notification.error({
                    title: `${MESSAGES.uploadError}: ${photo.file.name}`,
                });
                throw new Error();
            }
        },
        [notification, activePoint],
    );

    const handleFinish = async (values: FormValues) => {
        if (!activePoint) return;

        setSubmitLoading(true);
        try {
            const files = await Promise.all(
                (values.photos ?? []).map(uploadPhoto),
            );
            await postTaskCreateCustom({
                name: values.title,
                description: values.description,
                targetDate: dayjs(values.visitDate).format(
                    backendDateFormat.date,
                ),
                targetId: activePoint.id,
                targetType: "market",
                responseType: values.assignmentResponse,
                files,
                roles: values.roles,
                additionalData: { assignmentType: values.assignmentType },
            });

            notification.success({ title: MESSAGES.createSuccess });

            const pointIds = Object.keys(points);
            const roleSlug = capabilities.canUseConnectedAgentSchedule
                ? (ROLE_CHILDREN_MAP[role] as UserRole)
                : undefined;

            if (activeSchedule?.userId === agentId || points[activePoint.id]) {
                await Promise.all([
                    dispatch(
                        getWorkGetScheduleThunk({
                            userId: activeSchedule?.userId,
                            roleSlug,
                        }),
                    ),
                    dispatch(
                        getTaskListsThunk({
                            targetIds: pointIds,
                            roleSlug,
                        }),
                    ),
                ]);
            }

            onCloseModal();
        } catch {
            notification.error({ title: MESSAGES.createError });
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleValuesChange = (changed: Partial<FormValues>) => {
        if ("pointId" in changed && changed.pointId)
            setSelectedPointId(changed.pointId);
        if ("assignmentType" in changed)
            setAssignmentType(changed.assignmentType!);
        if ("roles" in changed) setRoles(changed.roles ?? []);
        if ("unplanned" in changed) setUnplanned(!!changed.unplanned);
    };

    const pointOptions = useMemo(
        () => routePoints.map((m) => ({ value: m.id, label: m.name })),
        [routePoints],
    );

    return (
        <Modal
            open
            title=""
            closable
            centered
            onCancel={onCloseModal}
            footer={null}
            width="100%"
            style={{ maxWidth: 400 }}
            destroyOnHidden
        >
            <PrimaryText
                textAlign="center"
                fontSize={16}
                fontWeight="500"
                padding="0 20px 0 0"
            >
                {isAgentMode
                    ? "Створення доручення"
                    : `Створення доручення для ТТ "${point?.name}"`}
            </PrimaryText>

            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinish={handleFinish}
                onValuesChange={handleValuesChange}
                initialValues={{
                    visitDate: today.toISOString(),
                    assignmentType: AssignmentType.Task,
                    assignmentResponse: AssignmentResponseType.Bool,
                    title: "",
                    description: "",
                    roles: [],
                    unplanned: false,
                    photos: initialPhotos,
                }}
            >
                <StyledCustomTaskForm>
                    {isAgentMode && (
                        <Form.Item
                            name="pointId"
                            label="Торгова точка"
                            rules={[
                                { required: true, message: MESSAGES.required },
                            ]}
                        >
                            <AppSelect
                                options={pointOptions}
                                placeholder="Оберіть торгову точку"
                                loading={isRoutePointsLoading}
                                disabled={isRoutePointsLoading}
                                showSearch={{ optionFilterProp: "label" }}
                                size="large"
                                optionRender={(option) => (
                                    <span
                                        style={{
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {option.label}
                                    </span>
                                )}
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="assignmentType"
                        label="Тип доручення"
                        rules={[{ required: true, message: MESSAGES.required }]}
                    >
                        <AppSelect
                            options={ASSIGNMENT_TYPE_OPTIONS}
                            placeholder="Оберіть тип"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="assignmentResponse"
                        label="Тип відповіді"
                        rules={[{ required: true, message: MESSAGES.required }]}
                    >
                        <AppSelect
                            options={ASSIGNMENT_RESPONSE_OPTIONS}
                            placeholder="Оберіть тип відповіді"
                            disabled={assignmentType in FIXED_RESPONSE_TYPES}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="title"
                        label="Назва доручення"
                        rules={[
                            { required: true, message: MESSAGES.required },
                            { min: 3, message: MESSAGES.minLength },
                        ]}
                    >
                        <Input
                            placeholder="Введіть назву доручення"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="roles"
                        label="Ролі"
                        rules={[{ required: true, message: MESSAGES.required }]}
                    >
                        <AppSelect
                            mode="multiple"
                            options={availableRoles}
                            placeholder="Оберіть ролі"
                            // Блокуємо поки маркет не вибраний або дати ще вантажаться
                            disabled={!activePoint || isLoadingDates}
                            loading={isLoadingDates}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="visitDate"
                        label="Дата"
                        rules={[{ required: true, message: MESSAGES.required }]}
                        getValueProps={(v) => ({ value: v ? dayjs(v) : null })}
                        getValueFromEvent={(d: dayjs.Dayjs | null) =>
                            d?.toISOString() ?? undefined
                        }
                    >
                        <DatePicker
                            style={{ width: "100%" }}
                            placeholder="Оберіть дату"
                            minDate={today}
                            disabledDate={shouldDisableDate}
                            allowClear={false}
                            // Блокуємо поки маркет не вибраний або дати ще вантажаться
                            disabled={!activePoint || isLoadingDates}
                            format={(val) =>
                                `${val.format("D MMMM YYYY")} року`
                            }
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="unplanned"
                        valuePropName="checked"
                        style={{ marginTop: -8 }}
                    >
                        <Checkbox disabled={isUnplannedForced}>
                            Позапланово
                        </Checkbox>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Опис"
                        rules={[{ required: true, message: MESSAGES.required }]}
                    >
                        <LazyRichTextEditorField />
                    </Form.Item>

                    <Form.Item name="photos" label="Фото">
                        <PhotoUploadInput buttonLabel="Додати фото" />
                    </Form.Item>
                </StyledCustomTaskForm>

                <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
                    <Flex gap={8} justify="flex-end">
                        {onBack && (
                            <BaseButton
                                onClick={onBack}
                                disabled={submitLoading}
                            >
                                Повернутися назад
                            </BaseButton>
                        )}
                        <BaseButton
                            type="primary"
                            htmlType="submit"
                            loading={submitLoading}
                            disabled={submitLoading}
                        >
                            Створити
                        </BaseButton>
                    </Flex>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CreateCustomTaskModal;
